import React, { useState, useEffect } from 'react';
import { Audit, AuditFinding } from '@/firebase/auditService';
import { auditService } from '@/firebase/auditService';
import { useNotification } from '@/contexts/NotificationContext';
import { ChevronDown, ChevronUp, Copy, Eye, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface AuditQuickReferencePanelProps {
  currentAudit: Audit;
  selectedComplianceTemplateId?: string | null; // If provided, filter findings for this template
  onCopyFinding?: (finding: AuditFinding) => void;
}

const AuditQuickReferencePanel: React.FC<AuditQuickReferencePanelProps> = ({
  currentAudit,
  selectedComplianceTemplateId,
  onCopyFinding
}) => {
  const { showNotification } = useNotification();
  const [previousAudits, setPreviousAudits] = useState<Audit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedAuditId, setExpandedAuditId] = useState<string | null>(null);
  const [selectedPreviousAudit, setSelectedPreviousAudit] = useState<Audit | null>(null);

  useEffect(() => {
    if (currentAudit.auditTemplateId && currentAudit.locationId) {
      loadPreviousAudits();
    }
  }, [currentAudit.auditTemplateId, currentAudit.locationId]);

  const loadPreviousAudits = async () => {
    if (!currentAudit.organizationId) return;

    setIsLoading(true);
    try {
      const result = await auditService.getAuditsForOrganization(currentAudit.organizationId);
      if (result.success && result.data) {
        // Filter audits:
        // 1. Same template
        // 2. Same location
        // 3. Completed status
        // 4. Different from current audit
        const filtered = result.data.filter(audit =>
          audit.auditTemplateId === currentAudit.auditTemplateId &&
          audit.locationId === currentAudit.locationId &&
          audit.status === 'completed' &&
          audit.id !== currentAudit.id
        );

        // Sort by completion date (most recent first)
        filtered.sort((a, b) => {
          const dateA = a.completedAt?.toMillis?.() || a.completedAt || 0;
          const dateB = b.completedAt?.toMillis?.() || b.completedAt || 0;
          return dateB - dateA;
        });

        setPreviousAudits(filtered.slice(0, 5)); // Show last 5 audits
      }
    } catch (error: any) {
      console.error('Error loading previous audits:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getFindingsForTemplate = (audit: Audit, templateId?: string | null): AuditFinding[] => {
    if (!audit.findings) return [];
    if (templateId) {
      return audit.findings.filter(f => f.complianceTemplateId === templateId);
    }
    return audit.findings;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'compliant': return 'bg-green-100 text-green-800';
      case 'non-compliant': return 'bg-red-100 text-red-800';
      case 'partial': return 'bg-yellow-100 text-yellow-800';
      case 'not_applicable': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleCopyFinding = (finding: AuditFinding) => {
    if (onCopyFinding) {
      onCopyFinding(finding);
      showNotification('Finding copied! You can now paste it in the findings section.', 'success');
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (previousAudits.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Previous Audits</h3>
        <p className="text-xs text-gray-500">No previous audits found for this template and location.</p>
      </div>
    );
  }

  const mostRecentAudit = previousAudits[0];
  const mostRecentFindings = getFindingsForTemplate(mostRecentAudit, selectedComplianceTemplateId);

  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Previous Audits</h3>
        <span className="text-xs text-gray-500">{previousAudits.length} available</span>
      </div>

      {/* Most Recent Audit Summary */}
      {mostRecentAudit && (
        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-sm font-medium text-gray-900">
                Most Recent: {formatDate(mostRecentAudit.completedAt)}
              </div>
              <div className="text-xs text-gray-600 mt-1">
                Score: {mostRecentAudit.completionPercentage || 0}% • 
                Findings: {mostRecentAudit.findings?.length || 0}
              </div>
            </div>
            <button
              onClick={() => {
                if (mostRecentAudit.id) {
                  window.open(`/audit/${mostRecentAudit.id}`, '_blank');
                }
              }}
              className="text-xs text-primary-600 hover:text-primary-800"
            >
              View →
            </button>
          </div>

          {/* Quick Findings Preview */}
          {mostRecentFindings.length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="text-xs font-medium text-gray-700">
                {selectedComplianceTemplateId ? 'Findings for this template:' : 'Recent Findings:'}
              </div>
              {mostRecentFindings.slice(0, 3).map((finding) => (
                <div key={finding.id} className="flex items-start justify-between bg-white p-2 rounded border border-gray-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(finding.status)}`}>
                        {finding.status}
                      </span>
                      <span className="text-xs text-gray-600">
                        {finding.complianceTemplateName}
                      </span>
                    </div>
                    {finding.notes && (
                      <p className="text-xs text-gray-700 line-clamp-2">{finding.notes}</p>
                    )}
                  </div>
                  {onCopyFinding && (
                    <button
                      onClick={() => handleCopyFinding(finding)}
                      className="ml-2 text-primary-600 hover:text-primary-800"
                      title="Copy finding"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
              {mostRecentFindings.length > 3 && (
                <p className="text-xs text-gray-500 text-center">
                  +{mostRecentFindings.length - 3} more findings
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* All Previous Audits (Collapsible) */}
      {previousAudits.length > 1 && (
        <div>
          <button
            onClick={() => setExpandedAuditId(expandedAuditId ? null : 'all')}
            className="w-full flex items-center justify-between text-xs text-gray-600 hover:text-gray-900 py-1"
          >
            <span>View all previous audits ({previousAudits.length - 1} more)</span>
            {expandedAuditId === 'all' ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          {expandedAuditId === 'all' && (
            <div className="mt-2 space-y-2">
              {previousAudits.slice(1).map((audit) => {
                const findings = getFindingsForTemplate(audit, selectedComplianceTemplateId);
                const isExpanded = expandedAuditId === audit.id;

                return (
                  <div key={audit.id} className="border border-gray-200 rounded p-2">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-xs font-medium text-gray-900">
                          {formatDate(audit.completedAt)}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          {audit.completionPercentage || 0}% • {audit.findings?.length || 0} findings
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            if (audit.id) {
                              window.open(`/audit/${audit.id}`, '_blank');
                            }
                          }}
                          className="text-xs text-primary-600 hover:text-primary-800"
                          title="View full audit"
                        >
                          <Eye className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => setExpandedAuditId(isExpanded ? null : audit.id || null)}
                          className="text-xs text-gray-600"
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-3 h-3" />
                          ) : (
                            <ChevronDown className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    </div>

                    {isExpanded && findings.length > 0 && (
                      <div className="mt-2 space-y-1 pt-2 border-t border-gray-200">
                        {findings.map((finding) => (
                          <div key={finding.id} className="flex items-start justify-between bg-gray-50 p-1.5 rounded text-xs">
                            <div className="flex-1">
                              <div className="flex items-center gap-1 mb-0.5">
                                <span className={`px-1.5 py-0.5 rounded ${getStatusColor(finding.status)}`}>
                                  {finding.status}
                                </span>
                              </div>
                              {finding.notes && (
                                <p className="text-gray-700 line-clamp-1">{finding.notes}</p>
                              )}
                            </div>
                            {onCopyFinding && (
                              <button
                                onClick={() => handleCopyFinding(finding)}
                                className="ml-1 text-primary-600"
                                title="Copy"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Comparison Indicator */}
      {mostRecentAudit && currentAudit.completionPercentage !== undefined && (
        <div className="pt-2 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">Score Trend:</span>
            <div className="flex items-center gap-1">
              {currentAudit.completionPercentage > (mostRecentAudit.completionPercentage || 0) ? (
                <>
                  <TrendingUp className="w-3 h-3 text-green-600" />
                  <span className="text-green-600 font-medium">
                    +{currentAudit.completionPercentage - (mostRecentAudit.completionPercentage || 0)}%
                  </span>
                </>
              ) : currentAudit.completionPercentage < (mostRecentAudit.completionPercentage || 0) ? (
                <>
                  <TrendingDown className="w-3 h-3 text-red-600" />
                  <span className="text-red-600 font-medium">
                    {currentAudit.completionPercentage - (mostRecentAudit.completionPercentage || 0)}%
                  </span>
                </>
              ) : (
                <>
                  <Minus className="w-3 h-3 text-gray-600" />
                  <span className="text-gray-600">No change</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditQuickReferencePanel;

